import { makeAutoObservable } from 'mobx'
import type { IIconsVector } from 'shared/ui/Icon/icons'
import { TagsControl } from 'entities/Tags/store/tagsControl'
import {
  stopConversationAction,
  type IAgentActionKey,
  type ITagActionItem,
} from 'entities/AIAgent/lib/agentActions'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  EmojiAction,
  mergeFieldReplacer,
  MessageFieldStore,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { EnumVariantMessageField } from 'widgets/MessageField/store/types'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import {
  formatWidgetMessage,
  type IWidgetQAPair,
} from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import { AutoControlStore } from './autoControlStore'
import type { InstructionsSectionStore } from './instructionsSectionStore'

export type IActionOption = {
  key: IAgentActionKey
  icon: IIconsVector
  label: string
}

export const YES_NO_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export const ACTION_OPTIONS: IActionOption[] = [
  { key: 'addTag', icon: 'tag', label: 'Add tag' },
  { key: 'removeTag', icon: 'tagRemove', label: 'Remove tag' },
  { key: 'addNote', icon: 'edit', label: 'Add contact note' },
  { key: 'sendSMS', icon: 'messageTextCircle02', label: 'Send SMS' },
  { key: 'stopConversation', icon: 'stop', label: 'Stop agent' },
]

const ACTIONS_WITH_TAG_STEP: IAgentActionKey[] = ['addTag', 'removeTag']
const ACTIONS_WITH_CONTENT_STEP: IAgentActionKey[] = ['addNote', 'sendSMS']

const Q_ADD_ACTIONS = 'Would you like to add any actions to this agent?'
const Q_WHICH_ACTION = 'Which action should the agent perform?'
const Q_NOTE_CONTENT = 'What note should the agent add?'
const Q_SMS_CONTENT = 'What SMS should the agent send?'
const Q_TRIGGER = 'When should this action be triggered?'

export class ActionsWidgetStore {
  step = 1
  focusedIndex = 0
  hasActions = false
  selectedActionKey: IAgentActionKey | null = null
  tagsControl = new TagsControl()
  triggerText = ''
  readonly noteAutoControl = new AutoControlStore()
  readonly noteContentStore: MessageFieldStore
  readonly smsAutoControl = new AutoControlStore()
  readonly smsContentStore: MessageFieldStore

  constructor(
    readonly sectionStore: InstructionsSectionStore,
    readonly chatStore: ChatStore,
    readonly entities: IEntitiesStore
  ) {
    this.noteContentStore = new MessageFieldStore(
      {
        placeholder: 'Write a note...',
        noCounter: true,
        styles: { minHeight: 28 },
        variant: EnumVariantMessageField.Note,
        textLimit: { maxLength: 255, message: 'Note can contain max 255 characters' },
        makeActions: () => [{ iconButtonComponent: <EmojiAction /> }],
        resetOnUnmount: false,
      },
      entities
    )

    this.smsContentStore = new MessageFieldStore(
      {
        placeholder: 'Write your message...',
        textLimit: { maxLength: 160, message: 'SMS can contain max 160 characters' },
        styles: { minHeight: 102 },
        resetOnUnmount: false,
        makeActions: () => [
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => {
                  if (!this.smsContentStore.addContent) return
                  this.smsContentStore.addContent(
                    mergeFieldReplacer(this.entities, value.template) + '&nbsp;'
                  )
                }}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
      },
      entities
    )

    makeAutoObservable(this)
  }

  get totalSteps() {
    if (!this.hasActions) return 1
    if (
      this.selectedActionKey !== null &&
      (ACTIONS_WITH_TAG_STEP.includes(this.selectedActionKey) ||
        ACTIONS_WITH_CONTENT_STEP.includes(this.selectedActionKey))
    ) {
      return 4
    }
    return 3
  }

  get isTagStep() {
    return (
      this.step === 3 &&
      this.selectedActionKey !== null &&
      ACTIONS_WITH_TAG_STEP.includes(this.selectedActionKey)
    )
  }

  get isAddTagStep() {
    return this.selectedActionKey === 'addTag'
  }

  get isNoteStep() {
    return this.step === 3 && this.selectedActionKey === 'addNote'
  }

  get isSMSStep() {
    return this.step === 3 && this.selectedActionKey === 'sendSMS'
  }

  get isLastStep() {
    return this.step === this.totalSteps && this.step > 1
  }

  get currentHeader() {
    if (this.step === 1) return Q_ADD_ACTIONS
    if (this.step === 2) return Q_WHICH_ACTION
    if (this.isTagStep && this.selectedActionKey) return this.getTagQuestion(this.selectedActionKey)
    if (this.isNoteStep) return Q_NOTE_CONTENT
    if (this.isSMSStep) return Q_SMS_CONTENT
    return Q_TRIGGER
  }

  get currentListLength() {
    if (this.step === 1) return YES_NO_OPTIONS.length
    if (this.step === 2) return ACTION_OPTIONS.length
    return 0
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  get isNextDisabled() {
    if (this.isLastStep) return !this.triggerText.trim()
    if (this.isTagStep) return this.tagsControl.ids.length === 0
    if (this.isNoteStep) return !this.noteAutoControl.isAuto && !this.noteContentStore.text.trim()
    if (this.isSMSStep) return !this.smsAutoControl.isAuto && !this.smsContentStore.text.trim()
    return false
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  setTriggerText = (value: string) => {
    this.triggerText = value
  }

  advanceStep = () => {
    this.step++
    this.focusedIndex = 0
  }

  goBack = () => {
    if (this.step <= 1) return
    this.step--
    this.focusedIndex = 0

    if (this.step === 1) {
      this.hasActions = false
    }
    if (this.step === 2) {
      this.selectedActionKey = null
      this.tagsControl = new TagsControl()
      this.triggerText = ''
      this._resetContentStores()
    }
  }

  reset = () => {
    this.step = 1
    this.focusedIndex = 0
    this.hasActions = false
    this.selectedActionKey = null
    this.tagsControl = new TagsControl()
    this.triggerText = ''
    this._resetContentStores()
  }

  private _resetContentStores = () => {
    const emptyData = {
      message: '',
      attachments: [],
      isReset: true,
      isReplace: true,
      isFocus: false,
    }
    this.noteAutoControl.setIsAuto(false)
    this.noteContentStore.setMessageData(emptyData)
    this.smsAutoControl.setIsAuto(false)
    this.smsContentStore.setMessageData(emptyData)
  }

  getTagQuestion = (key: IAgentActionKey) =>
    key === 'addTag' ? 'Which tag should the agent add?' : 'Which tag should the agent remove?'

  handleSelect = () => {
    if (this.step === 1) {
      const option = YES_NO_OPTIONS[this.safeIndex]
      if (option.id === 'no') {
        const message = formatWidgetMessage([{ q: Q_ADD_ACTIONS, a: 'No' }])
        this.chatStore.sendMessage(message)
        return
      }
      this.hasActions = true
      this.advanceStep()
    } else if (this.step === 2) {
      const action = ACTION_OPTIONS[this.safeIndex]
      this.selectedActionKey = action.key
      if (action.key === 'addNote') {
        this.noteContentStore.setFocusMessageFieldTrigger()
      }
      this.advanceStep()
    }
  }

  selectAt = (index: number) => {
    this.focusedIndex = index
    this.handleSelect()
  }

  handleClose = () => {
    const message = formatWidgetMessage([{ q: Q_ADD_ACTIONS, a: 'Skip' }])
    this.chatStore.sendMessage(message)
  }

  handleSubmit = () => {
    if (!this.selectedActionKey) return

    const actionOption = ACTION_OPTIONS.find((action) => action.key === this.selectedActionKey)
    const answer =
      this.selectedActionKey === 'stopConversation'
        ? stopConversationAction.template
        : actionOption?.label || ''
    const pairs: IWidgetQAPair[] = [
      { q: Q_ADD_ACTIONS, a: 'Yes' },
      { q: Q_WHICH_ACTION, a: answer },
    ]

    if (ACTIONS_WITH_TAG_STEP.includes(this.selectedActionKey)) {
      const tags: ITagActionItem[] = this.tagsControl.tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
      }))
      const tagTemplate =
        this.selectedActionKey === 'addTag'
          ? `@addTag(${JSON.stringify({ tags })})`
          : `@removeTag(${JSON.stringify({ tags })})`
      pairs.push({ q: this.getTagQuestion(this.selectedActionKey), a: tagTemplate })
    }

    if (ACTIONS_WITH_CONTENT_STEP.includes(this.selectedActionKey)) {
      const isNote = this.selectedActionKey === 'addNote'
      const autoControl = isNote ? this.noteAutoControl : this.smsAutoControl
      const contentStore = isNote ? this.noteContentStore : this.smsContentStore
      const rawContent = contentStore.text.trim()
      const mergeFields = [...rawContent.matchAll(/\{[^}]+\}/g)]
        .map((match) => this.entities.mergeFieldStore.getMergeFieldInfoByTemplate(match[0]))
        .filter((info) => info.mergeField !== null)

      if (!autoControl.isAuto && !rawContent) return

      const content = isNote ? rawContent : this.formatSmsContent(rawContent)
      const data = autoControl.isAuto ? { auto: true as const } : { content }
      const contentTemplate = isNote
        ? `@addNote(${JSON.stringify(data)})`
        : `@sendSMS(${JSON.stringify(data)})`
      const q = isNote ? Q_NOTE_CONTENT : Q_SMS_CONTENT
      pairs.push({ q, a: contentTemplate })

      if (mergeFields.length) {
        this.sectionStore.mergeContactFields(
          mergeFields.map((field) => ({
            key: field.fullMergeField,
            name: field?.mergeField?.label || '',
            type: field?.mergeField?.type || 'text',
          }))
        )
      }
    }

    pairs.push({ q: Q_TRIGGER, a: this.triggerText.trim() })

    const message = formatWidgetMessage(pairs)
    this.chatStore.sendMessage(message)
  }

  formatSmsContent = (content: string): string =>
    content.replace(/\{[^}]+\}/g, (match) => {
      const { mergeField, integrationKey } =
        this.entities.mergeFieldStore.getMergeFieldInfoByTemplate(match)
      if (!mergeField) return match
      const integrationName = this.entities.integrationsStore.getIntegration(integrationKey)?.name
      return integrationName ? `${mergeField.label} (${integrationName})` : mergeField.label
    })
}
